import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTag, faXmark } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../../utils/apiClient";
import { KIND_OPTIONS, OPERATOR_OPTIONS, CONDITION_TYPE_BY_FIELD, initialRuleForm } from "../constants";
import {
  buildConditionGroupsPayload,
  buildConditionsFromRows,
  emptyConditionGroup,
  emptyConditionRow,
  parseConditionGroups,
} from "../helpers";
import { ConditionRowBuilder, TagInput } from "./CommonComponents";

export default function RuleModal({ rule, allTags, onClose, onSaved }) {
  const isEditing = Boolean(rule);
  const [form, setForm] = useState(
    rule
      ? {
          name: rule.name,
          kind: rule.kind,
          days: rule.days,
          priority: rule.priority,
          isActive: rule.isActive,
          stopProcessing: rule.stopProcessing ?? false,
          reason: rule.reason ?? "",
          notes: rule.notes ?? "",
          tags: rule.tags ?? [],
        }
      : initialRuleForm
  );
  const [conditionGroups, setConditionGroups] = useState(
    rule ? parseConditionGroups(rule) : [emptyConditionGroup(0)]
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleConditionChange = (groupId, rowId, key, value) => {
    setConditionGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;
        const nextRows = group.rows.map((row) => {
          if (row.id !== rowId) return row;
          if (key === "field") {
            const type = CONDITION_TYPE_BY_FIELD[value] || "text";
            const defaultOperator = OPERATOR_OPTIONS[type][0].value;
            return { ...row, field: value, operator: defaultOperator, value: "" };
          }
          if (key === "operator") return { ...row, operator: value, value: "" };
          return { ...row, value };
        });
        return { ...group, rows: nextRows };
      })
    );
  };

  const handleGroupChange = (groupId, key, value) => {
    setConditionGroups((prev) =>
      prev.map((group, index) => {
        if (group.id !== groupId) return group;
        if (key === "groupJoinOperator" && index === 0) return group;
        return { ...group, [key]: value };
      })
    );
  };

  const addConditionGroup = () => {
    setConditionGroups((prev) => [...prev, emptyConditionGroup(prev.length)]);
  };

  const removeConditionGroup = (groupId) => {
    setConditionGroups((prev) => {
      if (prev.length === 1) return prev;
      const next = prev.filter((group) => group.id !== groupId);
      return next.map((group, index) => ({
        ...group,
        groupJoinOperator: index === 0 ? null : (group.groupJoinOperator || "AND"),
      }));
    });
  };

  const totalConditionRows = useMemo(() => {
    return conditionGroups.reduce((sum, group) => sum + (group.rows?.length || 0), 0);
  }, [conditionGroups]);

  const hasAdvancedGrouping = useMemo(() => {
    return conditionGroups.length > 1 || conditionGroups.some((g) => g.conditionLogicOperator === "OR");
  }, [conditionGroups]);

  const ensureLegacyConditionRows = () => {
    return conditionGroups.flatMap((group) => group.rows || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const conditionGroupsPayload = buildConditionGroupsPayload(conditionGroups);
      const legacyConditions = buildConditionsFromRows(ensureLegacyConditionRows());
      const payload = {
        name: form.name,
        kind: form.kind,
        days: Number(form.days),
        priority: Number(form.priority),
        isActive: form.isActive,
        stopProcessing: form.stopProcessing,
        reason: form.reason,
        notes: form.notes,
        tags: form.tags,
        conditions: legacyConditions,
        conditionGroups: conditionGroupsPayload,
      };
      const response = isEditing
        ? await apiClient.put(`/api/v1/difotTools/rules/${rule.id}`, payload)
        : await apiClient.post("/api/v1/difotTools/rules", payload);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || `Failed to ${isEditing ? "update" : "create"} rule`);
      }
      if (hasAdvancedGrouping) {
        toast.info("Condition groups saved. Rule evaluator must support conditionGroups to apply full AND/OR logic.");
      }
      toast.success(isEditing ? "Rule updated" : "Rule created");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 !mt-0">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl ring-1 ring-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {isEditing ? `Edit Rule - ${rule.name}` : "Add New Rule"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </button>
        </div>

        <form id="rule-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677]"
                placeholder="e.g. Sydney Metro Adjustment"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <FontAwesomeIcon icon={faTag} className="w-3 h-3 mr-1 opacity-60" />
                Tags
                <span className="ml-1 text-gray-400 font-normal">
                  (press Enter or comma to add - a rule can have multiple tags)
                </span>
              </label>
              <TagInput
                tags={form.tags}
                onChange={(tags) => setForm((p) => ({ ...p, tags }))}
                suggestions={allTags}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Kind</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677] bg-white"
                value={form.kind}
                onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))}
              >
                {KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Days</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677]"
                placeholder="1"
                value={form.days}
                onChange={(e) => setForm((p) => ({ ...p, days: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677]"
                placeholder="500"
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
              />
              <p className="text-[10px] text-gray-400 mt-0.5">Lower number = higher priority</p>
            </div>

            <div className="flex items-center gap-6 pt-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 accent-[#144677]"
                  checked={form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 accent-[#144677]"
                  checked={form.stopProcessing}
                  onChange={(e) => setForm((p) => ({ ...p, stopProcessing: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">Stop Processing</span>
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Conditions</label>
              <span className="text-[10px] text-gray-400">
                {conditionGroups.length} group{conditionGroups.length !== 1 ? "s" : ""} • {totalConditionRows} condition{totalConditionRows !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="space-y-3">
              {conditionGroups.map((group, index) => (
                <div key={group.id} className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-700">Group {index + 1}</span>
                      {index > 0 && (
                        <select
                          className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                          value={group.groupJoinOperator || "AND"}
                          onChange={(e) => handleGroupChange(group.id, "groupJoinOperator", e.target.value)}
                        >
                          <option value="AND">AND with previous</option>
                          <option value="OR">OR with previous</option>
                        </select>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400">Within group</span>
                      <select
                        className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                        value={group.conditionLogicOperator}
                        onChange={(e) => handleGroupChange(group.id, "conditionLogicOperator", e.target.value)}
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                      <button
                        type="button"
                        className="text-xs text-red-500 hover:text-red-600 disabled:opacity-40"
                        disabled={conditionGroups.length === 1}
                        onClick={() => removeConditionGroup(group.id)}
                      >
                        Remove group
                      </button>
                    </div>
                  </div>

                  <ConditionRowBuilder
                    rows={group.rows}
                    onChange={(rowId, key, value) => handleConditionChange(group.id, rowId, key, value)}
                    onAdd={() =>
                      setConditionGroups((prev) =>
                        prev.map((g) => (g.id === group.id ? { ...g, rows: [...g.rows, emptyConditionRow()] } : g))
                      )
                    }
                    onRemove={(rowId) =>
                      setConditionGroups((prev) =>
                        prev.map((g) => {
                          if (g.id !== group.id) return g;
                          if (g.rows.length === 1) return g;
                          return { ...g, rows: g.rows.filter((r) => r.id !== rowId) };
                        })
                      )
                    }
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={addConditionGroup}
                className="flex items-center gap-1.5 text-xs font-medium text-[#144677] hover:text-[#11385f] transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                Add Condition Group
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677] resize-none"
              rows={2}
              placeholder="Why this rule exists..."
              value={form.reason}
              onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677] resize-none"
              rows={2}
              placeholder="Optional notes about this rule..."
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="rule-form"
            disabled={isSaving}
            className="px-5 py-2 rounded-lg bg-[#144677] text-white text-sm font-medium hover:bg-[#11385f] disabled:opacity-60 transition-colors"
          >
            {isSaving ? "Saving..." : isEditing ? "Save Changes" : "Create Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}
